import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Tag } from 'lucide-react';
import axiosInstance from '../api/axios';
import BlogLikeButton from '../components/BlogLikeButton';
import BlogMostLovedSection from '../components/blog/BlogMostLovedSection';
import BlogDisclaimer from '../components/blog/BlogDisclaimer';
import BlogReaderQuestionBanner from '../components/BlogReaderQuestionBanner';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      let url = '/blog/posts?limit=20';
      if (selectedCategory) {
        url += `&category=${encodeURIComponent(selectedCategory)}`;
      }
      const response = await axiosInstance.get(url);
      setPosts(response.data || []);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/blog/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F0EB' }}>
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#8B7355] to-[#6D5A47] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Unser Blog</h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Entdecken Sie Tipps, Inspirationen und Neuigkeiten rund um unsere handgemachten Produkte
          </p>
        </div>
      </div>

      {/* Category Filter */}
      {categories.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-4 py-2 text-sm font-medium transition-colors min-h-[36px] ${
                !selectedCategory
                  ? 'bg-[#8B7355] text-white'
                  : 'bg-white text-[#5D4E3C] hover:bg-[#F5EDE4] border border-[#D4C4B0]'
              }`}
            >
              Alle Beiträge
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 text-sm font-medium transition-colors min-h-[36px] ${
                  selectedCategory === cat
                    ? 'bg-[#8B7355] text-white'
                    : 'bg-white text-[#5D4E3C] hover:bg-[#F5EDE4] border border-[#D4C4B0]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Blog Posts Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <BlogReaderQuestionBanner />
        <BlogMostLovedSection />
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B7355]"></div>
          </div>
        ) : posts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <article
                key={post.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
              >
                {/* Image */}
                <Link to={`/blog/${post.id}`}>
                  <div className="aspect-video overflow-hidden">
                    {post.image ? (
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#C4A084] to-[#8B7355] flex items-center justify-center">
                        <span className="text-white/60 text-6xl font-serif">H</span>
                      </div>
                    )}
                  </div>
                </Link>

                {/* Content */}
                <div className="p-6">
                  {/* Category & Date */}
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    {post.category && (
                      <span className="px-2 py-1 bg-[#F5F0EB] rounded text-[#8B7355] font-medium">
                        {post.category}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDate(post.created_at)}
                    </span>
                  </div>

                  {/* Title */}
                  <Link to={`/blog/${post.id}`}>
                    <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#8B7355] transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                  </Link>

                  {/* Excerpt */}
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <BlogLikeButton
                      postId={post.id}
                      initialLikes={post.likes || 0}
                      size="sm"
                    />
                    <Link
                      to={`/blog/${post.id}`}
                      className="flex items-center gap-1 text-[#8B7355] font-medium hover:gap-2 transition-all text-sm"
                    >
                      Weiterlesen
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📝</div>
            <h2 className="text-2xl font-bold text-gray-700 mb-2">Noch keine Beiträge</h2>
            <p className="text-gray-500">
              Bald erscheinen hier interessante Artikel und Neuigkeiten.
            </p>
          </div>
        )}
        <BlogDisclaimer />
      </div>
    </div>
  );
};

export default Blog;
